import React from 'react';
import { Toggle, StatusIndicator, Link } from '@amzn/awsui-components-react/polaris';
import CONSTANTS from 'src/utils/constants';
import { ErrorPopover } from 'src/components/tp-allocation/shared/DatasetGridCommon';
import { ACTIONS } from 'src/services/tp-allocation/data-set/DatasetReducer';
import { DatasetRecord } from 'src/models/tp-allocation/DatasetRecord';
import TPEAction from 'src/models/common/TPEAction';
import { DasDataSetRow, DasDataSetRowMenu } from './DasDataSetCells';

export interface DasDataSetColumnsArgs {
    viewMode: string;
    showDecimals: boolean;
    recordBeingEdited?: DatasetRecord;
    datasetDispatch: React.Dispatch<TPEAction>;
    onViewAllDataKeys: () => void;
    onUpdateValue?: (record: DatasetRecord) => void;
    /** Greys out every row menu, for example while a save or delete is in flight. */
    rowMenuDisabled?: boolean;
}

const header = (label: string) => () => <div className="cell-text-non-sortable">{label}</div>;

function RowStatus({ record }: { record: DasDataSetRow }) {
    const error = record.pullBalanceError || record.saveError
        || (record.errors != null && record.errors.size > 0 ? record.errors : null);
    if (error != null) {
        return <ErrorPopover error={error} />;
    }
    return record.showSavedIndicator
        ? <StatusIndicator data-class="smallStatusIndicator" type="success">Saved</StatusIndicator>
        : <span>&nbsp;</span>;
}

/** Column definitions of the Step 1 grid; columns without a Cell use the shared editable cell. */
export function getDasDataSetColumns(args: DasDataSetColumnsArgs) {
    const { viewMode, showDecimals, recordBeingEdited, datasetDispatch, onViewAllDataKeys, onUpdateValue, rowMenuDisabled } = args;
    const fields = CONSTANTS.DATA_SOURCE_FIELDS;
    return [
        { accessor: 'sequence', Header: header('#') },
        { accessor: fields.DATA_SOURCE.ACCESSOR, Header: header('Data source*') },
        { accessor: fields.PERIOD.ACCESSOR, Header: header('Period*') },
        { accessor: fields.FX_DATE.ACCESSOR, Header: header('FX date') },
        { accessor: fields.FX_TYPE.ACCESSOR, Header: header('FX type') },
        { accessor: `${fields.FX_RATE.ACCESSOR}Formatted`, Header: header('FX rate') },
        { accessor: fields.CURRENCY.ACCESSOR, Header: header('Currency') },
        {
            accessor: fields.DATA_KEY.ACCESSOR,
            Header: () => <div className="cell-text-non-sortable">Data key* <Link className="dataKeyViewAllLink cell-link"
                                                                                   onFollow={onViewAllDataKeys}>View All</Link></div>,
        },
        { accessor: fields.DESCRIPTION.ACCESSOR, Header: header('Description*') },
        { accessor: `${fields.VALUE.ACCESSOR}Formatted`, Header: header('Value') },
        {
            accessor: 'actions',
            Header: () => <div className="headerToggle">
                <Toggle className="showDecimalsToggle" onChange={() => datasetDispatch(ACTIONS.TOGGLE_SHOW_DECIMALS)} checked={showDecimals}>
                    <div className="cell-text-toggle">Show decimals</div>
                </Toggle>
            </div>,
            Cell: (e: { row: { original: DasDataSetRow } }) => <div className="rowActions">
                <RowStatus record={e.row.original} />
                {CONSTANTS.VIEW_MODE.FROZEN !== viewMode &&
                    <div className='contextMenuPosition'>
                        <DasDataSetRowMenu row={e.row.original} allowEditActions={CONSTANTS.VIEW_MODE.EDITABLE === viewMode}
                                           dispatch={datasetDispatch} recordBeingEdited={recordBeingEdited} onUpdateValue={onUpdateValue}
                                           disabled={rowMenuDisabled} />
                    </div>
                }
            </div>,
        },
    ];
}
